'use client'

// Next.js
import Link from 'next/link'

// Icons
import { ArrowRight as ArrowRightIcon } from 'lucide-react'

interface CTASectionProps {
  title: string
  description?: string
  buttonText: string
  buttonHref: string
  showArrow?: boolean
  variant?: 'blue' | 'teal' | 'purple' | 'gradient'
}

const variants = {
  blue: 'bg-blue-700 hover:bg-blue-500 focus:ring-blue-500',
  teal: 'bg-teal-600 hover:bg-teal-700 focus:ring-teal-500',
  purple: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500',
  gradient:
    'bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 focus:ring-teal-500',
}

export function CTASection({
  title,
  description,
  buttonText,
  buttonHref,
  showArrow = true,
  variant = 'gradient',
}: CTASectionProps) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-teal-200 bg-linear-to-br from-teal-50 to-blue-50 p-8 shadow-lg dark:border-teal-800/50 dark:from-teal-900/20 dark:to-blue-900/20">
      {/* Decorative background pattern */}
      <div className="absolute inset-0 opacity-10 dark:opacity-5">
        <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="32" height="32" patternUnits="userSpaceOnUse">
              <path d="M0 32V0h32" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative flex flex-col items-center justify-between md:flex-row md:gap-3">
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-3xl font-semibold text-gray-900 dark:text-white">{title}</h3>
          {description && (
            <p className="text-base text-gray-700 dark:text-gray-300">{description}</p>
          )}
        </div>

        <div className="shrink-0">
          <Link
            href={buttonHref}
            className={`inline-flex transform items-center justify-center rounded-full px-4 py-2 text-sm font-medium text-white no-underline shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl focus:ring-2 focus:ring-offset-2 focus:outline-none ${variants[variant]} `}
          >
            {buttonText}
            {showArrow && (
              <ArrowRightIcon className="ml-2 size-3 stroke-3 transition-transform group-hover:translate-x-1" />
            )}
          </Link>
        </div>
      </div>
    </div>
  )
}
